import { useState, useEffect } from 'react';
import { AgendaEditor } from '@/components/AgendaEditor';
import { FloatingWidget } from '@/components/FloatingWidget';
import { Reminders } from '@/components/coaching/Reminders';
import { useSessionStore } from '@/state/session-store';
import { useApi } from '@/lib/api';
import { checkBrowserSupport } from '@/services/deepgram-speech';
import { BrowserWarning } from '@/components/BrowserWarning';
import { openSessionPopup, isPopupOpen, clearStalePopupState } from '@/hooks/useBroadcastChannel';
import type { AgendaItem, SessionTiming, QuestionType, RehearsalDifficulty, MethodologyTemplate } from '@/types';

const DURATION_OPTIONS = [15, 30, 45, 60];
const MAX_TIME_OPTIONS = [
  { value: 60, label: '60 min' },
  { value: 90, label: '90 min' },
  { value: 120, label: '2 hrs' },
  { value: 0, label: 'Off' },
];

const DIFFICULTY_OPTIONS: { value: RehearsalDifficulty; label: string; description: string; disabled: boolean }[] = [
  { value: 'easy', label: 'Easy', description: 'Questions after each talking point', disabled: false },
  { value: 'medium', label: 'Medium', description: '15-second timer to answer', disabled: false },
  { value: 'hard', label: 'Hard', description: 'Voice interrupts (coming soon)', disabled: true },
];

const BASE_QUESTION_TYPES: { value: QuestionType; label: string }[] = [
  { value: 'talking_point', label: 'Talking Point Recall' },
  { value: 'technical', label: 'Technical Accuracy' },
];

const ADVANCED_QUESTION_TYPES: { value: QuestionType; label: string }[] = [
  { value: 'contrarian', label: 'Objection Handling' },
  { value: 'clarification', label: 'Clarification' },
  { value: 'edge_case', label: 'Edge Cases' },
];

/**
 * Preview component for a single agenda item
 */
function PreviewItem({ item }: { item: AgendaItem }) {
  const indent = item.isGroupHeader ? 0 : (item.level - 1) * 16;

  if (item.isGroupHeader) {
    return (
      <div className="mt-4 mb-2 first:mt-0">
        <div className="flex items-center gap-2 py-2 px-3 bg-slate-700/50 rounded-lg border-l-4 border-l-blue-500">
          <span className="text-blue-400 font-semibold text-sm uppercase tracking-wide">{item.text}</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex items-start gap-3 py-2 px-3 rounded-lg hover:bg-slate-700/30 transition-colors"
      style={{ marginLeft: indent }}
    >
      <span className="text-slate-500 text-sm mt-0.5">○</span>
      <span className="text-slate-300 text-sm">{item.text}</span>
    </div>
  );
}

/**
 * Settings panel (slide-out)
 */
function SettingsPanel({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const {
    coachingSettings,
    updateCoachingSettings,
    setSessionTiming,
    reminders,
    addReminder,
    toggleReminder,
    deleteReminder,
    isRehearsalMode,
    enableRehearsalMode,
    disableRehearsalMode,
    selectedMethodologyId,
    setSelectedMethodologyId,
  } = useSessionStore();
  const { get } = useApi();

  const [duration, setDuration] = useState(coachingSettings.defaultDurationMin);
  const [customDuration, setCustomDuration] = useState('');
  const [isCustom, setIsCustom] = useState(false);
  const [halfwayAlert, setHalfwayAlert] = useState(coachingSettings.enableHalfwayAlert);
  const [fiveMinAlert, setFiveMinAlert] = useState(coachingSettings.enableFiveMinAlert);
  const [maxCallTime, setMaxCallTime] = useState(coachingSettings.maxCallTimeMin);

  // Rehearsal mode state
  const [difficulty, setDifficulty] = useState<RehearsalDifficulty>('easy');
  const [questionFocus, setQuestionFocus] = useState<QuestionType[]>(['talking_point', 'technical']);

  // Methodology state
  const [methodologies, setMethodologies] = useState<MethodologyTemplate[]>([]);
  const [methodologiesLoading, setMethodologiesLoading] = useState(false);

  // Fetch methodologies when panel opens
  useEffect(() => {
    if (!isOpen || methodologies.length > 0) return;
    setMethodologiesLoading(true);
    get<{ methodologies: MethodologyTemplate[] }>('/methodologies')
      .then(data => setMethodologies(data.methodologies))
      .catch(err => console.warn('Failed to load methodologies:', err))
      .finally(() => setMethodologiesLoading(false));
  }, [isOpen]);

  if (!isOpen) return null;

  const handleDurationChange = (value: number | 'custom') => {
    if (value === 'custom') {
      setIsCustom(true);
      setCustomDuration(String(duration));
    } else {
      setIsCustom(false);
      setDuration(value);
    }
  };

  const handleModeToggle = (rehearsal: boolean) => {
    if (rehearsal) {
      enableRehearsalMode({ difficulty, questionFocus });
    } else {
      disableRehearsalMode();
    }
  };

  const handleDifficultyChange = (value: RehearsalDifficulty) => {
    setDifficulty(value);
    if (isRehearsalMode) {
      enableRehearsalMode({ difficulty: value, questionFocus });
    }
  };

  const handleQuestionTypeToggle = (type: QuestionType) => {
    const newFocus = questionFocus.includes(type)
      ? questionFocus.filter(t => t !== type)
      : [...questionFocus, type];
    if (newFocus.length === 0) return;
    setQuestionFocus(newFocus);
    if (isRehearsalMode) {
      enableRehearsalMode({ difficulty, questionFocus: newFocus });
    }
  };

  const handleSave = () => {
    const finalDuration = isCustom ? parseInt(customDuration) || 30 : duration;

    const timing: SessionTiming = {
      scheduledDurationMin: finalDuration,
      alerts: {
        halfway: halfwayAlert,
        fiveMinWarning: fiveMinAlert,
      },
    };

    updateCoachingSettings({
      maxCallTimeMin: maxCallTime,
      enableHalfwayAlert: halfwayAlert,
      enableFiveMinAlert: fiveMinAlert,
      defaultDurationMin: finalDuration,
    });

    setSessionTiming(timing);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Panel */}
      <div className="relative w-full max-w-md bg-slate-800 border-l border-slate-700 shadow-xl overflow-y-auto">
        <div className="sticky top-0 bg-slate-800 border-b border-slate-700 px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Session Settings</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Session Type */}
          <div className="space-y-2">
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
              Session Type
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => handleModeToggle(false)}
                className={`py-3 px-4 text-sm rounded-lg border-2 transition-all ${
                  !isRehearsalMode
                    ? 'bg-green-900/30 text-green-400 border-green-500'
                    : 'bg-slate-700 text-slate-300 border-slate-600 hover:border-slate-500'
                }`}
              >
                <div className="font-medium">Live Call</div>
                <div className="text-xs text-slate-400 mt-0.5">Track coverage</div>
              </button>
              <button
                onClick={() => handleModeToggle(true)}
                className={`py-3 px-4 text-sm rounded-lg border-2 transition-all ${
                  isRehearsalMode
                    ? 'bg-blue-900/30 text-blue-400 border-blue-500'
                    : 'bg-slate-700 text-slate-300 border-slate-600 hover:border-slate-500'
                }`}
              >
                <div className="font-medium">Rehearsal Mode</div>
                <div className="text-xs text-slate-400 mt-0.5">Practice with Q&A</div>
              </button>
            </div>
          </div>

          {/* Rehearsal Settings (only shown when rehearsal mode is active) */}
          {isRehearsalMode && (
            <>
              {/* Difficulty Selection */}
              <div className="space-y-2">
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
                  Difficulty
                </label>
                <div className="space-y-2">
                  {DIFFICULTY_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => !opt.disabled && handleDifficultyChange(opt.value)}
                      disabled={opt.disabled}
                      className={`w-full py-2.5 px-3 text-left rounded-lg border transition-all ${
                        opt.disabled
                          ? 'bg-slate-800 text-slate-500 border-slate-700 cursor-not-allowed'
                          : difficulty === opt.value
                            ? 'bg-blue-900/30 text-blue-400 border-blue-500'
                            : 'bg-slate-700 text-slate-300 border-slate-600 hover:border-blue-400'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm">{opt.label}</span>
                        {opt.disabled && (
                          <span className="text-xs bg-slate-700 text-slate-400 px-2 py-0.5 rounded">Soon</span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">{opt.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Question Types */}
              <div className="space-y-2">
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
                  Question Focus
                </label>
                <div className="space-y-2">
                  {BASE_QUESTION_TYPES.map(opt => (
                    <label
                      key={opt.value}
                      className="flex items-center gap-3 p-2.5 rounded-lg border border-slate-600 cursor-pointer hover:border-blue-400 transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={questionFocus.includes(opt.value)}
                        onChange={() => handleQuestionTypeToggle(opt.value)}
                        className="rounded border-slate-500 bg-slate-700 text-blue-500 focus:ring-blue-500"
                      />
                      <span className="text-sm text-slate-300">{opt.label}</span>
                    </label>
                  ))}
                  {difficulty !== 'easy' && (
                    <>
                      <div className="text-xs text-slate-500 uppercase tracking-wide mt-3 mb-1">
                        Advanced (Medium+)
                      </div>
                      {ADVANCED_QUESTION_TYPES.map(opt => (
                        <label
                          key={opt.value}
                          className="flex items-center gap-3 p-2.5 rounded-lg border border-orange-700/50 cursor-pointer hover:border-orange-500 transition-colors bg-orange-900/20"
                        >
                          <input
                            type="checkbox"
                            checked={questionFocus.includes(opt.value)}
                            onChange={() => handleQuestionTypeToggle(opt.value)}
                            className="rounded border-orange-600 bg-slate-700 text-orange-500 focus:ring-orange-500"
                          />
                          <span className="text-sm text-slate-300">{opt.label}</span>
                        </label>
                      ))}
                    </>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Sales Framework (only for live calls) */}
          {!isRehearsalMode && (
            <div className="space-y-2">
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
                Sales Framework
              </label>
              <p className="text-xs text-slate-500 -mt-1">
                Score your call against a methodology after the session
              </p>
              <div className="space-y-2">
                <button
                  onClick={() => setSelectedMethodologyId(null)}
                  className={`w-full py-2.5 px-3 text-left rounded-lg border transition-all ${
                    selectedMethodologyId === null
                      ? 'bg-green-900/30 text-green-400 border-green-500'
                      : 'bg-slate-700 text-slate-300 border-slate-600 hover:border-green-400'
                  }`}
                >
                  <span className="font-medium text-sm">None (Checklist Only)</span>
                  <p className="text-xs text-slate-500 mt-0.5">Track talking point coverage without methodology scoring</p>
                </button>
                {methodologiesLoading ? (
                  <div className="flex items-center gap-2 py-3 px-3 text-slate-500 text-sm">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-400" />
                    Loading frameworks...
                  </div>
                ) : (
                  methodologies.map(m => (
                    <button
                      key={m.id}
                      onClick={() => setSelectedMethodologyId(m.id)}
                      className={`w-full py-2.5 px-3 text-left rounded-lg border transition-all ${
                        selectedMethodologyId === m.id
                          ? 'bg-purple-900/30 text-purple-400 border-purple-500'
                          : 'bg-slate-700 text-slate-300 border-slate-600 hover:border-purple-400'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm">{m.name}</span>
                        <div className="flex items-center gap-1.5">
                          {m.category && (
                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                              m.category === 'qualification' ? 'bg-blue-900/50 text-blue-400' :
                              m.category === 'discovery' ? 'bg-emerald-900/50 text-emerald-400' :
                              'bg-orange-900/50 text-orange-400'
                            }`}>
                              {m.category}
                            </span>
                          )}
                          {m.complexity && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-600 text-slate-300">
                              {m.complexity}
                            </span>
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{m.description}</p>
                      <p className="text-[10px] text-slate-600 mt-1">
                        {m.elements.length} elements to score
                      </p>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Meeting Duration */}
          <div className="space-y-2">
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
              Meeting Duration
            </label>
            <div className="grid grid-cols-4 gap-2">
              {DURATION_OPTIONS.map(opt => (
                <button
                  key={opt}
                  onClick={() => handleDurationChange(opt)}
                  className={`py-2 px-3 text-sm rounded-md border transition-colors ${
                    !isCustom && duration === opt
                      ? 'bg-green-500 text-white border-green-500'
                      : 'bg-slate-700 text-slate-300 border-slate-600 hover:border-green-400'
                  }`}
                >
                  {opt} min
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-2">
              <button
                onClick={() => handleDurationChange('custom')}
                className={`py-2 px-3 text-sm rounded-md border transition-colors ${
                  isCustom
                    ? 'bg-green-500 text-white border-green-500'
                    : 'bg-slate-700 text-slate-300 border-slate-600 hover:border-green-400'
                }`}
              >
                Custom:
              </button>
              <input
                type="number"
                value={customDuration}
                onChange={(e) => {
                  setCustomDuration(e.target.value);
                  setIsCustom(true);
                }}
                onFocus={() => setIsCustom(true)}
                placeholder="45"
                min="5"
                max="180"
                className="w-20 px-2 py-1.5 text-sm bg-slate-700 border border-slate-600 rounded-md text-white focus:outline-none focus:ring-1 focus:ring-green-500"
              />
              <span className="text-sm text-slate-400">min</span>
            </div>
          </div>

          {/* Time Alerts */}
          <div className="space-y-2">
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
              Time Alerts
            </label>
            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={halfwayAlert}
                  onChange={(e) => setHalfwayAlert(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-500 bg-slate-700 text-green-500 focus:ring-green-500"
                />
                <span className="text-sm text-slate-300">Alert me at halfway point</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={fiveMinAlert}
                  onChange={(e) => setFiveMinAlert(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-500 bg-slate-700 text-green-500 focus:ring-green-500"
                />
                <span className="text-sm text-slate-300">Alert me at 5 minutes remaining</span>
              </label>
            </div>
          </div>

          {/* Max Recording Time */}
          <div className="space-y-2">
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
              Max Recording Time
            </label>
            <p className="text-xs text-slate-500 -mt-1">
              Auto-ends session to prevent forgotten recordings
            </p>
            <div className="grid grid-cols-4 gap-2">
              {MAX_TIME_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setMaxCallTime(opt.value)}
                  className={`py-2 px-3 text-sm rounded-md border transition-colors ${
                    maxCallTime === opt.value
                      ? 'bg-green-500 text-white border-green-500'
                      : 'bg-slate-700 text-slate-300 border-slate-600 hover:border-green-400'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Reminders */}
          <div className="border-t border-slate-700 pt-4">
            <Reminders
              reminders={reminders}
              onToggle={toggleReminder}
              onDelete={deleteReminder}
              onAdd={addReminder}
              readOnly={false}
              darkMode={true}
            />
            <p className="text-xs text-slate-500 mt-2">
              Add questions from last call, topics to cover, or follow-up items
            </p>
          </div>

          {/* Save Button */}
          <div className="pt-2">
            <button
              onClick={handleSave}
              className="w-full py-2.5 px-4 text-sm font-medium text-white bg-green-500 rounded-lg hover:bg-green-600 transition-colors"
            >
              Save Settings
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function SessionEditor() {
  const { agenda, agendaRawText, status, startSession, error } = useSessionStore();
  const { supported, reason } = checkBrowserSupport();
  const [showSettings, setShowSettings] = useState(false);
  const [showStartMenu, setShowStartMenu] = useState(false);

  // Clear any stale popup state on mount
  useEffect(() => {
    clearStalePopupState();
  }, []);

  const handleStartSession = () => {
    setShowStartMenu(false);
    startSession();
    // FloatingWidget will appear and handle the session
  };

  const handleStartInPopup = () => {
    setShowStartMenu(false);
    // Start session first, then open popup
    // openSessionPopup() handles checking for existing popups
    startSession();
    const popup = openSessionPopup();
    if (!popup) {
      // Popup was blocked or already open - session already started
      // so we'll fall back to showing the widget
      console.log('Popup not opened, falling back to widget');
    }
  };

  // Hide editor UI when session is active
  const isSessionActive = status === 'listening' || status === 'complete';

  const itemCount = agenda?.items.length || 0;
  const trackableCount = agenda?.items.filter(i => !i.isGroupHeader).length || 0;

  return (
    <div className="min-h-[calc(100vh-4rem)]">
      {/* Show editor UI when session is not active */}
      {!isSessionActive && (
        <>
          {/* Top bar */}
          <div className="sticky top-0 z-10 bg-slate-900/95 backdrop-blur border-b border-slate-700 px-6 py-4">
            <div className="max-w-7xl mx-auto flex items-center justify-between">
              <div>
                <h1 className="text-xl font-bold text-white">New Session</h1>
                <p className="text-sm text-slate-400">
                  {trackableCount > 0
                    ? `${trackableCount} talking point${trackableCount !== 1 ? 's' : ''} ready`
                    : 'Paste or type your talking points below'
                  }
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowSettings(true)}
                  className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white
                             bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors
                             flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Settings
                </button>
                {/* Start Session Button with Dropdown */}
                <div className="relative">
                  <div className="flex">
                    <button
                      onClick={handleStartSession}
                      disabled={!supported || trackableCount === 0}
                      className="px-4 py-2 text-sm font-medium text-white
                                bg-green-600 hover:bg-green-500 disabled:bg-slate-600 disabled:text-slate-400
                                rounded-l-lg transition-colors flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                      </svg>
                      Start Session
                    </button>
                    <button
                      onClick={() => setShowStartMenu(!showStartMenu)}
                      disabled={!supported || trackableCount === 0}
                      className="px-2 py-2 text-sm font-medium text-white
                                bg-green-600 hover:bg-green-500 disabled:bg-slate-600 disabled:text-slate-400
                                rounded-r-lg border-l border-green-500 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                  {/* Dropdown Menu */}
                  {showStartMenu && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowStartMenu(false)} />
                      <div className="absolute right-0 mt-2 w-56 bg-slate-800 border border-slate-600 rounded-lg shadow-xl z-20 overflow-hidden">
                        <button
                          onClick={handleStartSession}
                          className="w-full px-4 py-3 text-left text-sm text-slate-200 hover:bg-slate-700 flex items-center gap-3"
                        >
                          <svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                          </svg>
                          <div>
                            <div className="font-medium">Floating Widget</div>
                            <div className="text-xs text-slate-400">Overlay on your screen</div>
                          </div>
                        </button>
                        <button
                          onClick={handleStartInPopup}
                          className="w-full px-4 py-3 text-left text-sm text-slate-200 hover:bg-slate-700 flex items-center gap-3 border-t border-slate-700"
                        >
                          <svg className="w-5 h-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                          <div>
                            <div className="font-medium">Separate Window</div>
                            <div className="text-xs text-slate-400">Open in new popup window</div>
                          </div>
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Browser warning */}
          {!supported && (
            <div className="max-w-7xl mx-auto px-6 pt-4">
              <BrowserWarning reason={reason} />
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="max-w-7xl mx-auto px-6 pt-4">
              <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-4 text-red-400">
                {error}
              </div>
            </div>
          )}

          {/* Main content - Editor + Preview */}
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Editor Panel */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-sm font-medium text-slate-400 uppercase tracking-wide">Editor</h2>
                  <span className="text-xs text-slate-500">Tab to indent, Shift+Tab to outdent</span>
                </div>
                <AgendaEditor minHeight={400} maxHeight={600} />
              </div>

              {/* Preview Panel */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-sm font-medium text-slate-400 uppercase tracking-wide">Preview</h2>
                  {itemCount > 0 && (
                    <span className="text-xs text-slate-500">
                      How it will appear during your session
                    </span>
                  )}
                </div>
                <div className="bg-slate-800 border border-slate-600 rounded-lg overflow-hidden"
                     style={{ minHeight: 400, maxHeight: 600 }}>
                  {agenda && agenda.items.length > 0 ? (
                    <div className="p-4 overflow-y-auto" style={{ maxHeight: 600 }}>
                      <div className="space-y-1">
                        {agenda.items.map(item => (
                          <PreviewItem key={item.id} item={item} />
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-[400px] text-center px-8">
                      <div className="w-16 h-16 mb-4 rounded-full bg-slate-700 flex items-center justify-center">
                        <svg className="w-8 h-8 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <h3 className="text-slate-300 font-medium mb-2">No talking points yet</h3>
                      <p className="text-sm text-slate-500 max-w-xs">
                        Type or paste your agenda in the editor. Use Tab to indent sub-points and create structure.
                      </p>
                    </div>
                  )}
                </div>

                {/* Tips */}
                {!agendaRawText && (
                  <div className="mt-4 p-4 bg-slate-800/50 border border-slate-700 rounded-lg">
                    <h3 className="text-sm font-medium text-slate-300 mb-3">Quick Tips</h3>
                    <ul className="space-y-2 text-sm text-slate-400">
                      <li className="flex items-start gap-2">
                        <span className="text-blue-400">Section:</span>
                        <span>Lines ending with a colon or in ALL CAPS become section headers</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-green-400">Items:</span>
                        <span>Start with - or just type. One item per line.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-slate-300">Tab:</span>
                        <span>Indent for sub-items. Shift+Tab to outdent.</span>
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Settings Panel */}
          <SettingsPanel isOpen={showSettings} onClose={() => setShowSettings(false)} />
        </>
      )}

      {/* Show minimal page when session is active - FloatingWidget handles the UI */}
      {isSessionActive && (
        <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <div className="text-center">
            <div className="w-16 h-16 mb-4 mx-auto rounded-full bg-green-500/20 flex items-center justify-center">
              <svg className="w-8 h-8 text-green-400 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">Session in Progress</h2>
            {isPopupOpen() ? (
              <>
                <p className="text-slate-400 mb-4">
                  Your session is running in the popup window
                </p>
                <p className="text-sm text-slate-500">
                  Keep this window open to maintain the session
                </p>
              </>
            ) : (
              <>
                <p className="text-slate-400 mb-4">
                  Use the floating widget to monitor your talking points
                </p>
                <p className="text-sm text-slate-500">
                  Drag the widget to reposition it on your screen
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Floating widget - always renders when session is active (handles popup mode internally) */}
      {supported && isSessionActive && <FloatingWidget />}
    </div>
  );
}
